import { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useIntl } from '@babylon/intl'
import { useLocalisedContent, useConfig } from '@/redux/partnerConfig/selectors'
import { useUserConfig } from '@/redux/userConfig/selectors'
import { usePageContent } from '@/hooks'
import {
  setActiveStepIndex,
  setPageContent,
  setNextAction,
} from '@/redux/signup/actions'
import { setSignupProgress } from '@/redux/userConfig/actions'
import {
  useActiveStepIndex,
  useNextAction,
  useSignupRoutes,
} from '@/redux/signup/selectors'
import { Step } from '@/types/partnerConfig'

type UseOrchestratorType = {
  nextAction: () => void
  activeStepIndex: number
  pageContent: Step
  stepLabels: string[]
  pageComponentName: string
}

interface Props {
  stepSlug: string
}

export const useOrchestrator = ({ stepSlug }: Props): UseOrchestratorType => {
  const dispatch = useDispatch()
  const { locale } = useIntl()
  const signupRoutes = useSignupRoutes()
  const signupContent = useLocalisedContent(locale)
  const { steps } = useConfig()
  const history = useHistory()
  const { signupProgress } = useUserConfig()
  const activeStepIndex = useActiveStepIndex()

  const stepLabels: string[] = signupContent.steps.map(
    (page) => page.stepperLabel
  )
  const stepUrlSlugs = steps.map((step) => step.slug)

  const increaseSignupProgress = (stepIndex: number) => {
    const nextStepIndex = stepIndex + 1

    if (signupProgress <= stepIndex) {
      dispatch(setSignupProgress(nextStepIndex))
    }
  }

  // Action to progress to next step
  const onNextStepAction = (stepIndex: number) => {
    const nextStepSlug = stepUrlSlugs[stepIndex + 1]
    // Increase the signup progress index
    increaseSignupProgress(stepIndex)
    history.push(nextStepSlug)
  }

  // Set Page state from URL step slug
  useEffect(() => {
    if (stepSlug) {
      const newStepIndex = stepUrlSlugs.indexOf(stepSlug)
      const isNextStepSet = !!stepLabels[newStepIndex + 1]
      const nextAction = () =>
        isNextStepSet ? onNextStepAction(newStepIndex) : undefined
      dispatch(setActiveStepIndex(newStepIndex))
      dispatch(setPageContent(signupContent.steps[newStepIndex]))
      dispatch(setNextAction(nextAction))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepSlug])

  // Redirect to last reached step if needed
  useEffect(() => {
    // If the previous steps haven't been completed go back to the
    // latest step reached
    const latestStepReachedPath = signupRoutes?.[signupProgress]?.path

    if (activeStepIndex > signupProgress) {
      history.push(latestStepReachedPath)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStepIndex, signupProgress])

  return {
    nextAction: useNextAction(),
    pageContent: usePageContent(locale),
    pageComponentName: steps[activeStepIndex]?.component,
    activeStepIndex,
    stepLabels,
  }
}

export default useOrchestrator
